import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import CalendarWidget from "@/components/CalendarWidget";
import AdSlot from "@/components/AdSlot";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  LogOut,
  ShoppingBag,
  Store,
  Wallpaper as WallpaperIcon,
  Trash,
  Wallet as WalletIcon,
  X,
  GripVertical,
  FileText,
  ShieldCheck,
} from "lucide-react";
import NewTitleDialog from "@/components/NewTitleDialog";
import RenameDialog from "@/components/RenameDialog";
import NotificationBell from "@/components/NotificationBell";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

import WallpaperDialog from "@/components/WallpaperDialog";
import { useWallet, fmtEuro } from "@/lib/wallet";
import { useLongPressReorder } from "@/lib/useLongPressReorder";
import { useEdgeAutoScroll } from "@/lib/useEdgeAutoScroll";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COLORS = ["#2563EB", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444", "#06B6D4"];

export default function Sidebar({
  titles, activeId, onSelect, onCreate, onRename, onDelete, onOpenSearch,
  onJumpToItem, onReorder,
  mobileOpen = false, onMobileClose = () => {},
}) {
  const { user, logout } = useAuth();
  const wallet = useWallet();
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState(null);
  const [deleteTitle, setDeleteTitle] = useState(null);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  // Drag-reorder state. `dragId` = title being dragged, `overId` = row the
  // cursor is currently hovering, `overPos` = "above" | "below" for the drop
  // indicator line.
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overPos, setOverPos] = useState(null);

  const handleTitleDragStart = (e, tid) => {
    setDragId(tid);
    try {
      e.dataTransfer.effectAllowed = "move";
      // Use a non-"Files" marker so the ChatArea file-drop overlay doesn't fire.
      e.dataTransfer.setData("application/x-notelosh-title", tid);
    } catch { /* ignore */ }
  };
  const handleTitleDragOver = (e, tid) => {
    if (!dragId || dragId === tid) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientY - rect.top) < rect.height / 2 ? "above" : "below";
    setOverId(tid);
    setOverPos(pos);
  };
  const handleTitleDrop = (e, tid) => {
    e.preventDefault();
    if (!dragId || dragId === tid) { setDragId(null); setOverId(null); return; }
    const from = titles.findIndex((t) => t.title_id === dragId);
    let to = titles.findIndex((t) => t.title_id === tid);
    if (from < 0 || to < 0) { setDragId(null); setOverId(null); return; }
    if (overPos === "below") to += 1;
    if (from < to) to -= 1;
    const next = titles.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder?.(next.map((t) => t.title_id));
    setDragId(null); setOverId(null); setOverPos(null);
  };
  const handleTitleDragEnd = () => { setDragId(null); setOverId(null); setOverPos(null); };

  // Touch-friendly long-press reorder on mobile. Reuses the same visual
  // indicators as the HTML5 path by merging the two drag states together.
  const touch = useLongPressReorder({
    items: titles,
    idOf: (t) => t.title_id,
    onReorder: (next) => onReorder?.(next.map((t) => t.title_id)),
  });
  const effDragId = dragId || touch.dragId;
  const effOverId = overId || touch.overId;
  const effOverPos = overPos || touch.overPos;

  // Edge auto-scroll while an HTML5 drag is in progress — without this the
  // sidebar scroller stays frozen and the user can't reach off-screen rows.
  useEdgeAutoScroll(!!dragId);

  const initials = (user?.name || user?.email || "?").trim().slice(0, 1).toUpperCase();

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          data-testid="sidebar-backdrop"
          aria-label="Close menu"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`
          fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] shrink-0
          bg-[#0F0F11]/85 backdrop-blur-md border-r border-[#1E1E1E] flex flex-col h-full
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0 md:transform-none md:transition-none
        `}
      >
      {/* Header */}
      <div className="notelosh-sidebar-header px-3 sm:px-4 pt-5 pb-3 flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden shrink-0">
            <img src="/icons/logo-nl.png" alt="" className="h-6 w-6 object-contain" />
          </div>
          <span className="font-heading text-base sm:text-lg font-bold tracking-tight truncate">NoteLosh</span>
        </div>
        <div className="notelosh-header-actions flex items-center gap-0.5 sm:gap-1.5 shrink-0">
          <NotificationBell onJumpToItem={onJumpToItem} />
          <button
            data-testid="nav-market-btn"
            onClick={() => navigate("/market")}
            title="Marketplace"
            aria-label="Marketplace"
            className="h-9 w-9 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] transition-colors"
          >
            <Store className="h-4 w-4" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-btn"
                className="flex flex-col items-center gap-0.5 group"
              >
                <div className="h-9 w-9 rounded-full overflow-hidden group-hover:ring-2 group-hover:ring-[#27272A] transition-all">
                  <Avatar className="h-9 w-9">
                    {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
                    <AvatarFallback className="bg-[#1E1E1E] text-[#F3F4F6] text-sm">{initials}</AvatarFallback>
                  </Avatar>
                </div>
                <span
                  data-testid="sidebar-wallet-pill"
                  className="text-[9px] leading-none px-1.5 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 tabular-nums font-semibold min-w-[36px] text-center"
                  title="Wallet balance"
                >
                  {wallet ? fmtEuro(wallet.balance_cents) : "—"}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] w-60">
              <div className="px-2 py-2">
                <div className="text-sm font-medium truncate">{user?.name || "User"}</div>
                <div className="text-xs text-[#9CA3AF] truncate">{user?.email}</div>
                <div className="mt-2 flex items-center justify-between rounded-lg bg-[#1E1E1E] border border-[#27272A] px-2.5 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Wallet</span>
                  <span data-testid="dropdown-wallet-balance" className="text-sm font-semibold tabular-nums text-[#10B981]">
                    {wallet ? fmtEuro(wallet.balance_cents) : "—"}
                  </span>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                data-testid="nav-wallet-link"
                onClick={() => navigate("/wallet")}
                className="text-[#F3F4F6] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
              >
                <WalletIcon className="h-4 w-4 mr-2" /> Wallet &amp; cash-out
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="nav-wallpaper-link"
                onClick={() => setWallpaperOpen(true)}
                className="text-[#F3F4F6] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
              >
                <WallpaperIcon className="h-4 w-4 mr-2" /> Wallpaper
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="nav-trash-link"
                onClick={() => navigate("/trash")}
                className="text-[#F3F4F6] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
              >
                <Trash className="h-4 w-4 mr-2" /> Trash
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem
                  data-testid="nav-admin-link"
                  onClick={() => navigate("/admin")}
                  className="text-[#EF4444] focus:bg-[#1E1E1E] focus:text-[#F87171] cursor-pointer"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" /> Admin · Reports
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                data-testid="nav-terms-link"
                onClick={() => navigate("/terms")}
                className="text-[#9CA3AF] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer text-xs"
              >
                <FileText className="h-3.5 w-3.5 mr-2" /> Terms of Service
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="nav-privacy-link"
                onClick={() => navigate("/privacy")}
                className="text-[#9CA3AF] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer text-xs"
              >
                <FileText className="h-3.5 w-3.5 mr-2" /> Privacy Policy
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                data-testid="logout-menu-item"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="text-[#F3F4F6] focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                data-testid="delete-account-menu-item"
                onClick={() => setDeleteAccountOpen(true)}
                className="text-[#F87171] focus:bg-[#1E1E1E] focus:text-[#F87171] cursor-pointer"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Mobile close button — inline so it never overlaps the profile avatar */}
          <button
            data-testid="sidebar-close-btn"
            onClick={onMobileClose}
            aria-label="Close menu"
            className="md:hidden h-9 w-9 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] flex items-center justify-center text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search trigger */}
      <button
        data-testid="open-ai-search-btn"
        onClick={onOpenSearch}
        className="mx-4 mb-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-sm text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Ask AI to find anything…</span>
        <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#27272A] font-mono">⌘K</kbd>
      </button>

      {/* New note button — minimal ghost style */}
      <button
        data-testid="new-title-btn"
        onClick={() => setNewOpen(true)}
        className="mx-4 mb-2 flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg bg-transparent hover:bg-[#1E1E1E] border border-[#1E1E1E] hover:border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] text-xs font-medium transition-all"
      >
        <Plus className="h-3.5 w-3.5" /> New note
      </button>

      <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">
        Notes
      </div>

      {/* Notes list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
        {titles.length === 0 ? (
          <div className="px-3 py-6 text-sm text-[#6B7280] text-center">
            No notes yet. Create one to get started.
          </div>
        ) : (
          <ul className="space-y-0.5">
            {titles.map((t) => (
              <li
                key={t.title_id}
                data-reorder-id={t.title_id}
                onDragOver={(e) => handleTitleDragOver(e, t.title_id)}
                onDrop={(e) => handleTitleDrop(e, t.title_id)}
                className="relative"
              >
                {effOverId === t.title_id && effOverPos === "above" && (
                  <div className="absolute left-2 right-2 -top-[2px] h-0.5 rounded-full bg-[#3B82F6] pointer-events-none" />
                )}
                <div
                  draggable
                  onDragStart={(e) => handleTitleDragStart(e, t.title_id)}
                  onDragEnd={handleTitleDragEnd}
                  onTouchStart={touch.onTouchStart(t.title_id)}
                  data-testid={`title-row-${t.title_id}`}
                  className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all ${
                    effDragId === t.title_id ? "opacity-40 scale-[0.98]" : ""
                  } ${
                    activeId === t.title_id
                      ? "bg-[#1E1E1E] text-[#F3F4F6]"
                      : "text-[#9CA3AF] hover:bg-[#1E1E1E]/60 hover:text-[#F3F4F6]"
                  }`}
                  onClick={() => onSelect(t.title_id)}
                >
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
                    style={{ background: `linear-gradient(135deg, ${t.color}, ${t.color}99)` }}
                  >
                    {t.name?.[0]?.toUpperCase() || "T"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-xs text-[#6B7280] truncate">
                      {t.last_message || `${t.item_count || 0} items`}
                    </div>
                  </div>
                  <GripVertical
                    data-testid={`title-drag-${t.title_id}`}
                    className="h-4 w-4 shrink-0 text-[#4B5563] transition-opacity cursor-grab active:cursor-grabbing"
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`title-menu-${t.title_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded-md hover:bg-[#27272A] transition-all"
                      >
                        <MoreHorizontal className="h-4 w-4 text-[#9CA3AF]" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
                      <DropdownMenuItem
                        data-testid={`title-rename-${t.title_id}`}
                        onClick={(e) => { e.stopPropagation(); setRenameTitle(t); }}
                        className="focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
                      >
                        <Pencil className="h-4 w-4 mr-2" /> Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid={`title-delete-${t.title_id}`}
                        onClick={(e) => { e.stopPropagation(); setDeleteTitle(t); }}
                        className="text-[#FCA5A5] focus:bg-[#1E1E1E] focus:text-[#FCA5A5] cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {effOverId === t.title_id && effOverPos === "below" && (
                  <div className="absolute left-2 right-2 -bottom-[2px] h-0.5 rounded-full bg-[#3B82F6] pointer-events-none" />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <NewTitleDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreate={onCreate}
        colors={COLORS}
      />

      <RenameDialog
        title={renameTitle}
        onClose={() => setRenameTitle(null)}
        onSubmit={(patch) => onRename(renameTitle.title_id, patch)}
        colors={COLORS}
      />

      <AlertDialog open={!!deleteTitle} onOpenChange={(o) => !o && setDeleteTitle(null)}>
        <AlertDialogContent className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{deleteTitle?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              All notes and links in this title will move to the <b className="text-[#F3F4F6]">Trash</b> and stay there for
              <b className="text-[#F3F4F6]"> 3 days</b> — plenty of time to restore them if you change your mind. 😉
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTitle?.purchased_price_cents > 0 && (
            <div
              data-testid="paid-note-delete-warning"
              className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] px-4 py-3 text-sm flex items-start gap-2"
            >
              <span className="font-semibold shrink-0">⚠ ATTENTION:</span>
              <span>
                This is a <b>paid note</b> (€{(deleteTitle.purchased_price_cents / 100).toFixed(2)}).
                If you delete it, you&apos;ll have to <b>buy it again</b> to get it back.
              </span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1E1E1E] border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-title-btn"
              onClick={() => { onDelete(deleteTitle.title_id); setDeleteTitle(null); }}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="shrink-0 border-t border-[#1E1E1E] pt-2 pb-2">
        <CalendarWidget onJumpToItem={onJumpToItem} />
      </div>
    </aside>
    <WallpaperDialog open={wallpaperOpen} onOpenChange={setWallpaperOpen} />
    <DeleteAccountDialog
      open={deleteAccountOpen}
      onOpenChange={setDeleteAccountOpen}
      onDeleted={async () => {
        setDeleteAccountOpen(false);
        try { await logout(); } catch { /* ignore */ }
        navigate("/login", { replace: true });
      }}
    />
    </>
  );
}
